import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { ImageResizing, Utils } from 'poly-js-utils';

import { connectToJSContext } from '../utils.jsx';
import ProfileSocialIcons from './profile_social_icons';
import ProfileButton from './profile_button';
import ProfileSummary from './profile_summary';
import ProfilePhoto from './profile_photo';
import ProfileContact from './profile_contact';
import ProfileDesignations from './profile_designations';

import { formatProfileButtonUrl } from './selectors.js';
import { getProfileSummaryFromSiteData } from './profile_utils';

import {
    makeDesignationString,
    makeStateLicensesString,
    getHeroBackgroundPicture,
    Style
} from '../helpers.jsx';

const ProfileButtons = ({ buttons }) => (
    <div>
        {buttons.map((button, i) => (
            <ProfileButton
                url={button.url}
                text={button.text}
                openInNewWindow={button.openInNewWindow}
                key={i}
            />
        ))}
    </div>
);

const ProfileContent = (props) => {
    const summaryProps = _.pick(props,
        'professionalSummary',
        'statesLicensing',
        'statesLicensingLabel',
        'areasServed',
        'areasServedLabel',
        'languages',
        'languagesLabel',
        'includeBrokercheckLink'
    );

    const heroClasses = classNames(
        'col-xs-12 col-sm-8--half',
        { 'col-sm-push-4--half': props.photo && !props.fixHeroImageOrdering }
    );

    const profileButtonClasses = classNames(
        'visible-xs',
        { 'col-xs-8 col-xs-offset-2': props.photo && !props.fullWidthCtaButtons },
        { 'col-xs-12 col-xs-offset-0': !props.photo || props.fullWidthCtaButtons }
    );

    const topPhotoClasses = classNames(
        'col-xs-8 hidden col-xs-offset-2 init-custom-css__top',
        { 'visible-xs': props.isAlternativeMobileHeroEnabled }
    );

    const bottomPhotoClasses = classNames(
        'col-xs-8 col-sm-4--half col-xs-offset-2 col-sm-offset-0 init-custom-css__top',
        { 'hidden-xs': props.isAlternativeMobileHeroEnabled },
        { 'col-sm-pull-8--half': !props.fixHeroImageOrdering }
    );


    return (
        <div className="row">
            {props.photo && props.fixHeroImageOrdering &&
                <div className={bottomPhotoClasses} aria-label={props.name}>
                    <ProfilePhoto
                        photoUrl={props.photo}
                        name={props.name}
                    />
                </div>
            }

            <div className={heroClasses}>
                {props.photo && props.isAlternativeMobileHeroEnabled &&
                    <div className={topPhotoClasses} aria-label={props.name}>
                        <ProfilePhoto
                            photoUrl={props.photo}
                            name={props.name}
                        />
                    </div>
                }

                <div className="c-profile__header">
                    {props.name &&
                        <h1 className="c-profile__full-name">
                            {props.name}
                            {props.appendDesignationsToNameInHero &&
                                <ProfileDesignations isInline={true} />}
                        </h1>
                    }
                    {!props.appendDesignationsToNameInHero &&
                        <ProfileDesignations />}
                    {props.title &&
                        <p className="c-profile__title">{props.title}</p>
                    }
                    {props.networks &&
                        <ProfileSocialIcons
                            screenReaderText={props.networksScreenReaderText}
                            networks={props.networks}
                        />
                    }
                    {props.isAlternativeMobileHeroEnabled &&
                        <div className="visible-xs">
                            <ProfileSummary {...summaryProps} />
                        </div>
                    }
                </div>
                <div className="hidden-xs">
                    <ProfileSummary {...summaryProps} />

                    {props.isProfileContactDisplayed &&
                        <ProfileContact
                            phone={props.phone}
                            email={props.email}
                            address={props.address}
                        />
                    }
                </div>
                {props.buttons &&
                    <div className="hidden-xs">
                        <div className="c-profile__buttons profile-buttons">
                            <ProfileButtons
                                buttons={props.buttons}
                            />
                        </div>
                    </div>
                }
            </div>

            {props.isProfileContactDisplayed &&
                <div className="col-xs-12 visible-xs text-center">
                    <ProfileContact
                        phone={props.phone}
                        email={props.email}
                        address={props.address}
                    />
                    {props.buttons &&
                        <div className={profileButtonClasses}>
                            <ProfileButtons
                                buttons={props.buttons}
                            />
                        </div>
                    }
                </div>
            }

            {props.photo && !props.fixHeroImageOrdering &&
                <div className={bottomPhotoClasses}>
                    <ProfilePhoto
                        photoUrl={props.photo}
                        name={props.name}
                    />
                </div>
            }

            {props.buttons && !props.isProfileContactDisplayed &&
                <div className={profileButtonClasses}>
                    <ProfileButtons
                        buttons={props.buttons}
                    />
                </div>
            }
            <div className={classNames('col-xs-12 hidden', { 'visible-xs': !props.isAlternativeMobileHeroEnabled })}>
                <ProfileSummary {...summaryProps} />
            </div>
        </div>
    );
};

const Profile = (props) => {
    const { homeBackgroundImageUrl } = props;
    return (
        <div className="c-profile">
            {homeBackgroundImageUrl &&
                /* eslint-disable max-len */
                <Style>{`
                .c-profile__wrapper {
                    background-image: url("${homeBackgroundImageUrl}");
                    background-image: -webkit-image-set(url("${homeBackgroundImageUrl}") 1x, url("${homeBackgroundImageUrl}") 2x, url("${homeBackgroundImageUrl}") 3x);
                }
            `}</Style>
                /* eslint-enable max-len */
            }
            <div className="c-profile__wrapper">
                <div className="container">
                    <ProfileContent {...props} />
                </div>
            </div>
        </div>
    );
};

Profile.propTypes = {
    designationString: PropTypes.string,
    title: PropTypes.string,
    isProfileContactDisplayed: PropTypes.bool,
    phone: PropTypes.string,
    email: PropTypes.string,
    address: PropTypes.shape({
        street: PropTypes.string.isRequired,
        suite: PropTypes.string,
        city: PropTypes.string.isRequired,
        state: PropTypes.string,
        zip_code: PropTypes.string
    }),
    buttons: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
            openInNewWindow: PropTypes.bool
        })
    ),
    professionalSummary: PropTypes.string,
    statesLicensing: PropTypes.string,
    statesLicensingLabel: PropTypes.string,
    areasServed: PropTypes.string,
    areasServedLabel: PropTypes.string,
    languagesLabel: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.string),
    photo: PropTypes.string,
    networks: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string.isRequired,
            name: PropTypes.string,
            url: PropTypes.string.isRequired,
            id: PropTypes.string,
            icon: PropTypes.string
        })
    ),
    networksScreenReaderText: PropTypes.string,
    includeBrokercheckLink: PropTypes.bool,
    isAlternativeMobileHeroEnabled: PropTypes.bool,
    appendDesignationsToNameInHero: PropTypes.bool,
    fixHeroImageOrdering: PropTypes.bool
};

const mapStateToProps = ({ profile, theme_data, categories, base_link }) => {
    const useTeamNameInHero = Utils.selectBoxIsTrue(theme_data.use_team_name_in_hero);

    const name = useTeamNameInHero && profile.team_name ?
        profile.team_name :
        profile.name || `${profile.first_name} ${profile.last_name}`;

    const themeDataCtaButtons = theme_data.home_page.cta_header_buttons || [];
    const scheduleAppointmentLinkParams = profile.schedule_appointment_link;
    const ctaButtons = scheduleAppointmentLinkParams && scheduleAppointmentLinkParams.url
        ? [...themeDataCtaButtons, scheduleAppointmentLinkParams]
        : themeDataCtaButtons;
    return {
        name,
        isProfileContactDisplayed: Utils.selectBoxIsTrue(theme_data.home_page.is_profile_contact_displayed),
        phone: profile.phones.phone,
        email: profile.email,
        address: profile.address,
        designationString: makeDesignationString(
            profile.designations,
            categories.designations,
            profile.additional_designations
        ),
        title: profile.title,
        buttons: ctaButtons.map((button) =>
            Object.assign(button, {
                openInNewWindow: Utils.selectBoxIsTrue(button.open_in_new_window),
                url: formatProfileButtonUrl(base_link, button)
            })
        ),
        isAlternativeMobileHeroEnabled: Utils.selectBoxIsTrue(theme_data.is_alternative_mobile_hero_enabled),
        fullWidthCtaButtons: Utils.selectBoxIsTrue(theme_data.home_page.is_cta_button_full_width),
        fixHeroImageOrdering: Utils.selectBoxIsTrue(theme_data.home_page.is_hero_image_ordering_aoda_fix_enabled),
        appendDesignationsToNameInHero: Utils.selectBoxIsTrue(theme_data.append_designations_to_name_in_hero),
        professionalSummary: getProfileSummaryFromSiteData(theme_data, profile),
        statesLicensing: profile.states_licensing &&
            makeStateLicensesString(profile.states_licensing, profile),
        statesLicensingLabel: (theme_data.labels && theme_data.labels.profile_states_licensing) ||
            'States Licensed',
        areasServed: profile.areas_served,
        areasServedLabel: (theme_data.labels && theme_data.labels.profile_areas_served) || 'Areas Served',
        languages: profile.languages,
        languagesLabel: (theme_data.labels && theme_data.labels.profile_languages) ||
            'Languages Spoken',
        includeBrokercheckLink: Utils.selectBoxIsTrue(theme_data.footer.include_brokercheck_link),
        homeBackgroundImageUrl: getHeroBackgroundPicture(
            profile.profile_background,
            theme_data.home_page.header_background_image,
            theme_data.header_background_image_list
        ),
        photo: profile.profile_photo ?
            ImageResizing.resize(profile, 'profile_photo', 400) :
            theme_data.home_page.default_profile_photo ?
                ImageResizing.resize(theme_data.home_page, 'default_profile_photo', 400) :
                null,
        networks: profile.networks && [
            {
                type: 'Facebook',
                name: profile.networks.facebook_name,
                url: profile.networks.facebook_url,
                id: profile.networks.facebook_id,
                icon: 'fa-facebook-official'
            },
            {
                type: 'LinkedIn',
                name: profile.networks.linkedin_name,
                url: profile.networks.linkedin_url,
                id: profile.networks.linkedin_id,
                icon: 'fa-linkedin-square'
            },
            {
                type: 'Twitter',
                name: profile.networks.twitter_name,
                url: profile.networks.twitter_url,
                id: profile.networks.twitter_id,
                icon: 'fa-twitter-square'
            },
            {
                type: 'Instagram',
                name: profile.networks.instagram_name,
                url: profile.networks.instagram_url,
                id: profile.networks.instagram_id,
                icon: 'fa-instagram'
            },
            {
                type: 'Yelp',
                name: profile.networks.yelp_name,
                url: profile.networks.yelp_url,
                icon: 'fa-yelp-square'
            }
        ],
        networksScreenReaderText: `for ${name}`
    };
};

export default connectToJSContext(mapStateToProps)(Profile);



// WEBPACK FOOTER //
// ./components/profile/index.jsx